/**
 * Fix Queue — vulnerable families in the order you should work them.
 *
 * One row per family (not per version — the Breaches tab has that view),
 * scored threat × exposure × actionability on the backend and grouped into
 * five bands. Band 1 is worked before band 2; inside a band, rows are in
 * score order. Every row carries ONE primary action — the thing that
 * actually resolves it — plus a menu for recording a decision (snooze,
 * assign, accept the risk) so an item you consciously parked does not
 * resurface at the top tomorrow.
 *
 * Snoozed / accepted rows drop out of the default "Active" view but stay
 * counted in the strip above the bands — the audit trail is the point.
 *
 * The queue is fetched ONCE (state=all) and the state filter is applied
 * client-side: the backend walk behind it is the expensive part of the
 * whole Vulnerabilities page on a large estate, and a filter click should
 * never trigger it.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  RefreshCw, Flame, ChevronRight, ChevronDown, MoreHorizontal, Zap, PackagePlus,
  Package, ShieldOff, HelpCircle, Clock, UserCheck, ShieldCheck, RotateCcw,
  ExternalLink, Info,
} from "lucide-react";
import { PosturePill } from "./RemediationPostureWidget";

const CARD_BG =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] " +
  "border border-[rgba(229,239,250,0.1)] rounded-xl";

const BTN_GHOST =
  "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10";

const SEVERITY_COLOR = {
  CRITICAL: "bg-red-600 text-white",
  HIGH: "bg-orange-600 text-white",
  MEDIUM: "bg-yellow-600 text-white",
  LOW: "bg-blue-600 text-white",
};

// Left-edge stripe + heading colour per band. Mirrors the backend BAND_ORDER.
const BAND_TONE = {
  do_now:          { stripe: "border-l-red-500",     text: "text-red-300",     chip: "bg-red-600 text-white" },
  one_click:       { stripe: "border-l-emerald-400", text: "text-emerald-300", chip: "bg-emerald-600 text-white" },
  needs_packaging: { stripe: "border-l-amber-400",   text: "text-amber-300",   chip: "bg-amber-500 text-black" },
  needs_packaging_no_cve: { stripe: "border-l-sky-400", text: "text-sky-300",  chip: "bg-sky-700 text-white" },
  handled:         { stripe: "border-l-white/30",    text: "text-white/70",    chip: "bg-white/15 text-white" },
};

// What the primary button says and does, keyed on the backend's next_action.
const ACTION_META = {
  enable_auto_update: { label: "Enable auto-update", icon: Zap,         tone: "bg-emerald-600 hover:bg-emerald-500 text-white" },
  create_package:     { label: "Create package",     icon: PackagePlus, tone: "bg-cyan-600 hover:bg-cyan-500 text-white" },
  update_package:     { label: "Update package",     icon: Package,     tone: "bg-cyan-600 hover:bg-cyan-500 text-white" },
  verify_rollout:     { label: "View package",       icon: ShieldCheck, tone: BTN_GHOST },
  map_cpe:            { label: "Map CPE",            icon: HelpCircle,  tone: "bg-amber-600 hover:bg-amber-500 text-white" },
};

const STATE_META = {
  in_progress: { label: "In progress", cls: "bg-cyan-700 text-white" },
  snoozed:     { label: "Snoozed",     cls: "bg-white/15 text-white" },
  accepted:    { label: "Risk accepted", cls: "bg-purple-700 text-white" },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return null;
  }
};

const isoDaysFromNow = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
};

const KpiTile = ({ label, value, sub, tone = "default", onClick, testId }) => {
  const toneClasses = {
    default: "text-white",
    danger: "text-red-300",
    warning: "text-orange-300",
    good: "text-emerald-300",
  }[tone] || "text-white";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      className={`${CARD_BG} p-4 text-left ${onClick ? "hover:bg-white/[0.08] transition cursor-pointer" : ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wide text-white/60">{label}</div>
      <div className={`text-2xl font-semibold leading-tight ${toneClasses}`}>{value}</div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </Tag>
  );
};

const ThreatCell = ({ row }) => {
  const sev = (row.highest_severity || "").toUpperCase();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        {sev ? (
          <Badge className={`${SEVERITY_COLOR[sev] || "bg-gray-600 text-white"} text-[10px]`}>{sev}</Badge>
        ) : (
          <Badge className="bg-amber-600/80 text-white text-[10px]" title="Matched CVEs have no CVSS score yet">Unscored</Badge>
        )}
        {row.has_kev && (
          <Badge className="bg-rose-700 text-white text-[10px] inline-flex items-center gap-1" title="Actively exploited (CISA KEV)">
            <Flame className="w-3 h-3" /> KEV
          </Badge>
        )}
      </div>
      <div className="text-[11px] text-white/60 font-mono">
        {row.highest_cvss != null ? `CVSS ${Number(row.highest_cvss).toFixed(1)}` : "CVSS —"}
        {" · "}
        {row.max_epss != null ? `EPSS ${(row.max_epss * 100).toFixed(1)}%` : "EPSS —"}
      </div>
    </div>
  );
};

const OverdueCell = ({ row }) => {
  const d = row.days_overdue || 0;
  if (row.is_breach) {
    const tone = d >= 60 ? "bg-red-800" : d >= 30 ? "bg-red-600" : "bg-orange-600";
    return (
      <span className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold text-white ${tone}`}
        title={`${d} days since first exposed — beyond the Cyber Essentials 14-day window`}>
        {d}d overdue
      </span>
    );
  }
  return (
    <span className="inline-flex rounded px-2 py-0.5 text-xs font-semibold bg-white/10 text-white/70"
      title={row.has_fix === false ? "No fixed version published yet" : "Inside the 14-day window"}>
      {d}d · {row.has_fix === false ? "no fix" : "in window"}
    </span>
  );
};

const ScoreCell = ({ row }) => (
  <span
    className="font-mono text-sm text-white tabular-nums cursor-help"
    title={
      `threat ${row.priority_threat} × exposure ${row.priority_exposure} × action ${row.priority_action_weight}\n` +
      "threat: max(CVSS×10, EPSS×100) +15 if CE breach; KEV = 100\n" +
      "exposure: 1 + log10(devices)\n" +
      "action: 1.3 one toggle · 1.0 needs packaging · 0.4 ALICE handles it"
    }
    data-testid="priority-score"
  >
    {row.priority_score}
  </span>
);

/**
 * Record a decision on one row. ``mode`` = "accept" | "in_progress".
 */
const DecisionDialog = ({ open, mode, row, onClose, onSubmit }) => {
  const [reason, setReason] = useState("");
  const [expiryDays, setExpiryDays] = useState("90");
  const [owner, setOwner] = useState("");
  const [ticket, setTicket] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(row?.queue_action?.reason || "");
      setOwner(row?.queue_action?.owner || "");
      setTicket(row?.queue_action?.ticket_ref || "");
      setExpiryDays("90");
    }
  }, [open, row]);

  const isAccept = mode === "accept";
  const canSubmit = isAccept ? reason.trim().length > 0 : (owner.trim() || ticket.trim());

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(
        isAccept
          ? {
              state: "accepted",
              reason: reason.trim(),
              expires_at: expiryDays === "never" ? null : isoDaysFromNow(Number(expiryDays)),
            }
          : { state: "in_progress", owner: owner.trim() || null, ticket_ref: ticket.trim() || null }
      );
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0f172a] border-white/15 text-white sm:max-w-md" data-testid="queue-decision-dialog">
        <DialogHeader>
          <DialogTitle>{isAccept ? "Accept the risk" : "Mark in progress"}</DialogTitle>
          <DialogDescription className="text-white/60">
            {row?.family_name}
            {isAccept
              ? " — recorded with a reason and an expiry so it reads as a decision, not an oversight."
              : " — who has this, and where it is being tracked."}
          </DialogDescription>
        </DialogHeader>
        {isAccept ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wide">Reason (required)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Air-gapped lab machines; compensating control in place."
                className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30"
                rows={3}
                data-testid="accept-reason-input"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wide">Review again in</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {[["30", "30 days"], ["90", "90 days"], ["180", "6 months"], ["never", "No expiry"]].map(([v, l]) => (
                  <Button
                    key={v}
                    size="sm"
                    variant={expiryDays === v ? "default" : "outline"}
                    onClick={() => setExpiryDays(v)}
                    className={expiryDays === v ? "bg-cyan-600 hover:bg-cyan-500 text-white" : BTN_GHOST}
                  >
                    {l}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wide">Owner</label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Name or team"
                className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="progress-owner-input" />
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wide">Ticket reference</label>
              <Input value={ticket} onChange={(e) => setTicket(e.target.value)} placeholder="e.g. CHG0012345 or JIRA-812"
                className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-white/30" data-testid="progress-ticket-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className={BTN_GHOST}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || busy} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="queue-decision-submit">
            {isAccept ? "Accept risk" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PriorityQueueTab = ({ onJumpToUnmapped }) => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [stateFilter, setStateFilter] = useState("active");
  // Band id -> collapsed. "Handled by ALICE" starts collapsed: it is the
  // band that, by definition, needs nothing from the reader.
  const [collapsed, setCollapsed] = useState({ handled: true });
  const [dialog, setDialog] = useState({ open: false, mode: "accept", row: null });
  const [busyFamily, setBusyFamily] = useState(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/priority`, {
        headers, params: { state: "all", page_size: 1000 },
      });
      setData(r.data || null);
    } catch (err) {
      console.warn("priority queue load failed", err);
      toast.error("Failed to load the Fix Queue");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  // Mirrors the backend's filter_queue so the two views agree.
  const visibleItems = useMemo(() => {
    const items = data?.items || [];
    if (stateFilter === "all") return items;
    if (stateFilter === "active") return items.filter((r) => r.queue_state === "open" || r.queue_state === "in_progress");
    return items.filter((r) => r.queue_state === stateFilter);
  }, [data, stateFilter]);

  const rowsByBand = useMemo(() => {
    const out = {};
    visibleItems.forEach((r) => {
      (out[r.band] = out[r.band] || []).push(r);
    });
    return out;
  }, [visibleItems]);

  const setDecision = async (row, body) => {
    const headers = await authHeaders();
    try {
      await axios.put(`${API}/cve/priority/${encodeURIComponent(row.family_name)}/action`, body, { headers });
      toast.success(
        body.state === "snoozed" ? `${row.family_name} snoozed until ${fmtDate(body.snooze_until)}`
          : body.state === "accepted" ? `Risk accepted for ${row.family_name}`
          : `${row.family_name} marked in progress`
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the decision");
      throw err;
    }
  };

  const reopen = async (row) => {
    try {
      const headers = await authHeaders();
      await axios.delete(`${API}/cve/priority/${encodeURIComponent(row.family_name)}/action`, { headers });
      toast.success(`${row.family_name} reopened`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reopen");
    }
  };

  const runPrimary = async (row) => {
    switch (row.next_action) {
      case "enable_auto_update": {
        if (!row.package_id) {
          toast.error("No package id on this row — open Packages to enable auto-update");
          return;
        }
        setBusyFamily(row.family_name);
        try {
          const headers = await authHeaders();
          await axios.post(`${API}/packages/${encodeURIComponent(row.package_id)}/toggle-auto-update`, null, {
            headers, params: { enabled: true },
          });
          toast.success(`Auto-update enabled for ${row.family_name} — future releases roll out automatically`);
          load();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not enable auto-update");
        } finally {
          setBusyFamily(null);
        }
        return;
      }
      case "create_package": {
        const appName = row.app_name || row.family_name;
        // Same hand-off Discover uses: a Winget match opens the automated
        // packaging modal; otherwise the custom-upload flow (customer
        // supplies the installer) so nothing is ever "blocked".
        navigate("/catalog", {
          state: row.winget_id
            ? { autoPackage: true, wingetId: row.winget_id, fromDiscover: true, discoverFamilyName: row.family_name }
            : { customUpload: true, appName, familyName: row.family_name, fromDiscover: true, targetVersion: row.worst_fix_version || null },
        });
        return;
      }
      case "update_package":
      case "verify_rollout":
        navigate("/packages");
        return;
      case "map_cpe":
        onJumpToUnmapped?.();
        return;
      default:
        return;
    }
  };

  const bands = data?.bands || [];
  const sc = data?.state_counts || {};
  const needsHuman = (sc.open || 0) + (sc.in_progress || 0);
  const oneClick = bands.find((b) => b.band === "one_click");
  const mapping = data?.mapping_coverage || {};

  return (
    <div className="space-y-4" data-testid="priority-queue-tab">
      {/* KPI strip */}
      <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Needs a human"
          value={needsHuman.toLocaleString()}
          sub={`${sc.open || 0} open · ${sc.in_progress || 0} in progress`}
          tone={needsHuman > 0 ? "danger" : "good"}
          testId="kpi-queue-open"
        />
        <KpiTile
          label="One-click wins"
          value={(oneClick?.active || 0).toLocaleString()}
          sub={oneClick?.active ? `${(oneClick.devices || 0).toLocaleString()} devices cleared by a toggle` : "No stale packages waiting"}
          tone={oneClick?.active ? "good" : "default"}
          onClick={oneClick?.active ? () => setCollapsed((c) => ({ ...c, one_click: false })) : undefined}
          testId="kpi-queue-oneclick"
        />
        <KpiTile
          label="Parked"
          value={((sc.snoozed || 0) + (sc.accepted || 0)).toLocaleString()}
          sub={`${sc.snoozed || 0} snoozed · ${sc.accepted || 0} risk accepted`}
          onClick={() => setStateFilter("all")}
          testId="kpi-queue-parked"
        />
        <KpiTile
          label="Unmapped families"
          value={(mapping.unmapped_families || 0).toLocaleString()}
          sub="No CPE — not in this queue. Click to map."
          tone={mapping.unmapped_families > 0 ? "warning" : "default"}
          onClick={onJumpToUnmapped}
          testId="kpi-queue-unmapped"
        />
      </div>

      {/* Toolbar */}
      <div className={`${CARD_BG} p-3 flex flex-wrap items-center gap-2 justify-between`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-white/60 uppercase tracking-wide">Show</span>
          {[
            ["active", "Active"],
            ["in_progress", "In progress"],
            ["snoozed", "Snoozed"],
            ["accepted", "Risk accepted"],
            ["all", "Everything"],
          ].map(([val, label]) => (
            <Button
              key={val}
              variant={stateFilter === val ? "default" : "outline"}
              onClick={() => setStateFilter(val)}
              size="sm"
              className={stateFilter === val ? "bg-cyan-600 hover:bg-cyan-500 text-white" : BTN_GHOST}
              data-testid={`queue-state-filter-${val}`}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-white/50 hidden md:inline-flex items-center gap-1">
            <Info className="w-3 h-3" /> Bands are worked top to bottom; rows inside a band are in score order.
          </span>
          <Button variant="outline" size="sm" onClick={load} className={BTN_GHOST} data-testid="queue-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {loading && !data && (
        <div className={`${CARD_BG} p-8 text-center text-white/70`}>Loading…</div>
      )}

      {/* Bands */}
      {bands.map((band) => {
        const rows = rowsByBand[band.band] || [];
        const tone = BAND_TONE[band.band] || BAND_TONE.handled;
        // Band 4 has no threat to show; its columns are the estate-sprawl ones.
        const isNoCve = band.band === "needs_packaging_no_cve";
        const isCollapsed = !!collapsed[band.band];
        return (
          <div
            key={band.band}
            className={`${CARD_BG} overflow-hidden border-l-4 ${tone.stripe}`}
            data-testid={`queue-band-${band.band}`}
          >
            <button
              className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.04]"
              onClick={() => setCollapsed((c) => ({ ...c, [band.band]: !isCollapsed }))}
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />}
              <span className={`font-mono text-2xl font-black leading-none ${tone.text} tabular-nums`}>{band.index}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-semibold text-white">{band.label}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${tone.chip}`}>
                    {rows.length} {rows.length === 1 ? "family" : "families"}
                  </span>
                  {band.devices > 0 && (
                    <span className="text-[11px] text-white/50">{band.devices.toLocaleString()} devices</span>
                  )}
                </div>
                <div className="text-xs text-white/55 mt-0.5">{band.rule}</div>
              </div>
            </button>

            {!isCollapsed && (
              rows.length === 0 ? (
                <div className="px-5 pb-4 text-sm text-white/50">Nothing in this band for the current view.</div>
              ) : (
                <div className="max-h-[560px] overflow-auto border-t border-white/[0.08]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b-[rgba(229,239,250,0.1)] hover:bg-transparent">
                        <TableHead className="text-white font-semibold">Family</TableHead>
                        {isNoCve ? (
                          <>
                            <TableHead className="text-white font-semibold w-[90px] text-right">Versions</TableHead>
                            <TableHead className="text-white font-semibold w-[100px] text-right">Installs</TableHead>
                            <TableHead className="text-white font-semibold w-[120px] text-right">Behind latest</TableHead>
                            <TableHead className="text-white font-semibold w-[150px]">ALICE posture</TableHead>
                            <TableHead className="text-white font-semibold w-[130px]">Fix version</TableHead>
                            <TableHead className="text-white font-semibold w-[80px] text-right">Sprawl</TableHead>
                          </>
                        ) : (
                          <>
                            <TableHead className="text-white font-semibold w-[170px]">Threat</TableHead>
                            <TableHead className="text-white font-semibold w-[90px] text-right">Devices</TableHead>
                            <TableHead className="text-white font-semibold w-[120px]">Exposure</TableHead>
                            <TableHead className="text-white font-semibold w-[150px]">ALICE posture</TableHead>
                            <TableHead className="text-white font-semibold w-[130px]">Fix version</TableHead>
                            <TableHead className="text-white font-semibold w-[70px] text-right">Score</TableHead>
                          </>
                        )}
                        <TableHead className="text-white font-semibold w-[230px] text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => {
                        const am = ACTION_META[r.next_action] || ACTION_META.create_package;
                        const AIcon = am.icon;
                        const stateMeta = r.queue_state !== "open" ? STATE_META[r.queue_state] : null;
                        const qa = r.queue_action || {};
                        return (
                          <TableRow
                            key={r.family_name}
                            className="border-b-[rgba(229,239,250,0.06)] hover:bg-white/[0.04]"
                            data-testid={`queue-row-${r.family_name}`}
                          >
                            <TableCell>
                              <div className="font-medium text-white flex items-center gap-2 flex-wrap">
                                <Link
                                  to={`/discover/governance/${encodeURIComponent(r.family_name)}`}
                                  className="hover:text-cyan-300 inline-flex items-center gap-1"
                                  title="Open family governance"
                                >
                                  {r.app_name || r.family_name}
                                  <ExternalLink className="w-3 h-3 opacity-50" />
                                </Link>
                                {stateMeta && (
                                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${stateMeta.cls}`}>
                                    {stateMeta.label}
                                  </span>
                                )}
                                {r.queue_state_expired && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-600 text-white"
                                    title="A snooze or risk acceptance on this family has expired, so it is back in the queue.">
                                    Back in queue
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-white/50">
                                {isNoCve
                                  ? `${r.version_count || 1} ${r.version_count === 1 ? "version" : "versions"} in estate · no CVE matched today`
                                  : `${r.version_count || 0} vulnerable ${r.version_count === 1 ? "version" : "versions"} · ${r.cve_count || 0} CVE${r.cve_count === 1 ? "" : "s"}`}
                                {qa.owner && ` · ${qa.owner}`}
                                {qa.ticket_ref && ` · ${qa.ticket_ref}`}
                                {r.queue_state === "snoozed" && qa.snooze_until && ` · until ${fmtDate(qa.snooze_until)}`}
                                {r.queue_state === "accepted" && (qa.expires_at ? ` · review ${fmtDate(qa.expires_at)}` : " · no expiry")}
                              </div>
                              {r.queue_state === "accepted" && qa.reason && (
                                <div className="text-[11px] text-purple-200/80 mt-0.5 italic max-w-[36ch] truncate" title={qa.reason}>
                                  “{qa.reason}”
                                </div>
                              )}
                            </TableCell>
                            {isNoCve ? (
                              <>
                                <TableCell className="text-right text-white/90 font-medium tabular-nums">{r.version_count || 1}</TableCell>
                                <TableCell className="text-right text-white/90 tabular-nums">{(r.installs || 0).toLocaleString()}</TableCell>
                                <TableCell className="text-right text-white/85 tabular-nums">
                                  {(r.devices_not_on_latest || 0).toLocaleString()}
                                  {r.sprawl_quadrant && <div className="text-[10px] text-white/45">{r.sprawl_quadrant.replace("_", " ")}</div>}
                                </TableCell>
                                <TableCell><PosturePill posture={r.posture} /></TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell><ThreatCell row={r} /></TableCell>
                                <TableCell className="text-right text-white/90 font-medium tabular-nums">
                                  {(r.device_count || 0).toLocaleString()}
                                </TableCell>
                                <TableCell><OverdueCell row={r} /></TableCell>
                                <TableCell><PosturePill posture={r.posture} /></TableCell>
                              </>
                            )}
                            <TableCell className="font-mono text-xs text-white/85">
                              {r.worst_fix_version
                                ? `≥ ${r.worst_fix_version}`
                                : <span className="text-white/60" title="NVD has not recorded a fixed version for the matched CVEs — the current release is usually clean. Package the latest.">latest release</span>}
                              {r.winget_id && (
                                <div className="text-[10px] text-cyan-300/80 font-sans" title={`Winget ${r.winget_id} — ALICE can build and keep this updated without an installer upload`}>automatable</div>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {isNoCve
                                ? <span className="font-mono text-sm text-white tabular-nums cursor-help" title="Estate Sprawl score — version chaos × devices behind latest; 0 for a single-version family, then ordered by installs">{r.sprawl_score ? r.sprawl_score.toFixed(1) : "—"}</span>
                                : <ScoreCell row={r} />}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="inline-flex items-center gap-1 justify-end">
                                <Button
                                  size="sm"
                                  onClick={() => runPrimary(r)}
                                  disabled={busyFamily === r.family_name}
                                  className={am.tone}
                                  data-testid={`queue-action-${r.family_name}`}
                                >
                                  <AIcon className={`w-4 h-4 mr-1 ${busyFamily === r.family_name ? "animate-pulse" : ""}`} />
                                  {am.label}
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="icon" variant="outline" className={`h-8 w-8 ${BTN_GHOST}`} aria-label="More actions" data-testid={`queue-menu-${r.family_name}`}>
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-[#0f172a] border-white/15 text-white">
                                    <DropdownMenuLabel className="text-white/50 text-xs">Record a decision</DropdownMenuLabel>
                                    <DropdownMenuItem onClick={() => setDecision(r, { state: "snoozed", snooze_until: isoDaysFromNow(7) })}>
                                      <Clock className="w-4 h-4 mr-2" /> Snooze 7 days
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDecision(r, { state: "snoozed", snooze_until: isoDaysFromNow(30) })}>
                                      <Clock className="w-4 h-4 mr-2" /> Snooze 30 days
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDialog({ open: true, mode: "in_progress", row: r })}>
                                      <UserCheck className="w-4 h-4 mr-2" /> Mark in progress…
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setDialog({ open: true, mode: "accept", row: r })}>
                                      <ShieldOff className="w-4 h-4 mr-2" /> Accept risk…
                                    </DropdownMenuItem>
                                    {r.queue_state !== "open" && (
                                      <>
                                        <DropdownMenuSeparator className="bg-white/10" />
                                        <DropdownMenuItem onClick={() => reopen(r)}>
                                          <RotateCcw className="w-4 h-4 mr-2" /> Reopen
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )
            )}
          </div>
        );
      })}

      {data && visibleItems.length === 0 && !loading && (
        <div className={`${CARD_BG} p-8 text-center text-white/70`} data-testid="queue-empty">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
          Nothing needs a human right now.
          {stateFilter === "active" && ((sc.snoozed || 0) + (sc.accepted || 0)) > 0 && (
            <div className="text-xs text-white/50 mt-1">
              {(sc.snoozed || 0) + (sc.accepted || 0)} parked item(s) — switch to “Everything” to see them.
            </div>
          )}
        </div>
      )}

      <DecisionDialog
        open={dialog.open}
        mode={dialog.mode}
        row={dialog.row}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onSubmit={(body) => setDecision(dialog.row, body)}
      />
    </div>
  );
};

export default PriorityQueueTab;
